import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getMe, type AuthUser } from '../api/client';

/**
 * Anmeldung ueber ein httponly-Session-Cookie vom Backend (PowerID/OIDC laeuft
 * dort). Anders als bei powerCONTACT gibt es KEIN Token im Browser: nichts zu
 * speichern, nichts anzuhaengen, kein 401-Interceptor. Ein 401 heisst schlicht
 * „nicht angemeldet" — ProtectedRoute schickt zur Anmeldeseite.
 *
 * Rollen (PowerID-Gruppen, Backend): admin | user.
 *   canManageUsers / canManageSystem / canSeeAdminArea  = admin
 */
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  canManageUsers: boolean;
  canManageSystem: boolean;
  canSeeAdminArea: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von <AuthProvider> genutzt werden');
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      setUser(await getMe());
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await getMe();
        if (!cancelled) setUser(u);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const logout = useCallback(async () => {
    // 21.10.2026: Abmelden ueber das Backend — es loescht die Cookies, meldet auch bei PowerID ab und landet auf
    // /login?abgemeldet=1 (das Kennzeichen verhindert die sofortige Weiterleitung zu PowerID).
    // KEIN setUser(null) vorher: das rendert sofort die Routen-Absicherung neu (→ /login?next=/), deren
    // Auto-Weiterleitung zu /api/auth/oidc/login ueberschreibt dann diese Navigation (Browser-Test powerid-Session).
    window.location.assign('/api/auth/oidc/logout');
  }, []);

  const role = user?.role;
  const isAdmin = role === 'admin';
  const canManageUsers = isAdmin;
  const canManageSystem = isAdmin;

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      isAdmin, canManageUsers, canManageSystem,
      canSeeAdminArea: canManageUsers || canManageSystem,
      logout, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
